import React, { useState } from 'react';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { toast } from 'react-toastify';
import { X, Paperclip, Download } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IMedia {
  id: number;
  model_type: string;
  model_id: number;
  collection_name: string;
  name: string;
  file_name: string;
  mime_type: string;
  size: number;
  path: string;
  url?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const STATUS_BADGE_CLASS: Record<string, string> = {
  DRAFT: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  SUBMITTED: 'bg-blue-100 text-blue-700 border-blue-300',
  APPROVED: 'bg-green-100 text-green-700 border-green-300',
  REJECTED: 'bg-red-100 text-red-700 border-red-300',
  REVISION: 'bg-orange-100 text-orange-700 border-orange-300',
};

export const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  REVISION: 'Revision',
};

// ─── Name map ─────────────────────────────────────────────────────────────────

export interface NameMap {
  keyActivities: Record<number, string>;
  jobDescs: Record<number, string>;
}

export async function fetchKeyActivityName(
  tenantId: string | number,
  kaId: number,
): Promise<{ id: number; name: string }> {
  try {
    const r = await axiosInstance.get(`${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}/${kaId}`);
    return { id: kaId, name: (r.data?.data ?? r.data)?.name ?? '' };
  } catch {
    return { id: kaId, name: '' };
  }
}

export async function fetchJobDescName(tenantId: string | number, jdId: number): Promise<{ id: number; name: string }> {
  try {
    const r = await axiosInstance.get(`${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/${jdId}`);
    return { id: jdId, name: (r.data?.data ?? r.data)?.name ?? '' };
  } catch {
    return { id: jdId, name: '' };
  }
}

export async function resolveNameMap(
  tenantId: string | number,
  items: { org_key_activity_id: number; org_job_description_id: number }[],
): Promise<NameMap> {
  const uniqueKaIds = [...new Set(items.map(i => i.org_key_activity_id).filter(Boolean))] as number[];
  const uniqueJdIds = [...new Set(items.map(i => i.org_job_description_id).filter(Boolean))] as number[];

  const [kaResults, jdResults] = await Promise.all([
    Promise.all(uniqueKaIds.map(id => fetchKeyActivityName(tenantId, id))),
    Promise.all(uniqueJdIds.map(id => fetchJobDescName(tenantId, id))),
  ]);

  return {
    keyActivities: Object.fromEntries(kaResults.map(r => [r.id, r.name])),
    jobDescs: Object.fromEntries(jdResults.map(r => [r.id, r.name])),
  };
}

// ─── Utilities ────────────────────────────────────────────────────────────────

export function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export async function downloadKpiMedia(mediaId: number, fileName: string): Promise<void> {
  try {
    const downloadUrl = `${env.apiTasklistBaseUrl}/api/v1/compro/media/${mediaId}`;
    const response = await axiosInstance.get(downloadUrl, { responseType: 'blob' });
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch {
    toast.error('Gagal mengunduh file');
  }
}

// ─── Components ───────────────────────────────────────────────────────────────

export function StatusBadge({ status }: { status?: string | boolean }) {
  const key = typeof status === 'string' ? status.toUpperCase() : String(status).toUpperCase();
  const cls = STATUS_BADGE_CLASS[key] ?? 'bg-gray-100 text-gray-600 border-gray-300';
  const label = STATUS_LABEL[key] ?? status ?? '-';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {label}
    </span>
  );
}

export function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex text-sm items-center gap-1">
      <span className="text-gray-500 w-40 flex-shrink-0">{label}</span>
      <span className="text-gray-800">:</span>
      <span className="text-gray-800">{value}</span>
    </div>
  );
}

// ─── IApprovedKpiItem ─────────────────────────────────────────────────────────

export interface IApprovedKpiItem {
  id: number;
  org_key_activity_id: number;
  org_job_description_id: number;
  weight: number;
  target: number;
  actual_value: number | null;
  manager_actual?: number | null;
  aob?: number | null;
  score?: number | null;
  notes: string;
  media: IMedia[];
  org_key_activity?: { id: number; name: string };
  org_job_description?: { id: number; name: string };
}

// ─── ApprovalStatusBanner ─────────────────────────────────────────────────────

const APPROVAL_STATUS_BANNER_CONFIG: Record<
  string,
  { bg: string; border: string; text: string; icon: string; message: string }
> = {
  APPROVED: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    icon: '✓',
    message: 'KPI ini sudah disetujui.',
  },
  REVISION: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-800',
    icon: '↩',
    message: 'KPI ini sudah dikembalikan untuk revisi.',
  },
};

export function ApprovalStatusBanner({ status }: Readonly<{ status?: string }>) {
  const key = status?.toUpperCase() ?? '';
  const config = APPROVAL_STATUS_BANNER_CONFIG[key] ?? {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-700',
    icon: 'ℹ',
    message: 'Aksi tidak tersedia untuk status ini.',
  };
  return (
    <div
      className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg border ${config.bg} ${config.border}`}
      role="status"
    >
      <span className={`text-base font-semibold leading-none ${config.text}`}>{config.icon}</span>
      <span className={`text-sm font-medium ${config.text}`}>{config.message}</span>
    </div>
  );
}

// ─── EvidenceModal ────────────────────────────────────────────────────────────

export function EvidenceModal({ item, onClose }: Readonly<{ item: IApprovedKpiItem; onClose: () => void }>) {
  const hasMedia = item.media && item.media.length > 0;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Bukti KPI"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-800">Bukti / Evidence</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Tutup modal"
          >
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {hasMedia ? (
            <ul className="space-y-2">
              {item.media.map(media => (
                <li
                  key={media.id}
                  className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate">{media.name || media.file_name}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{formatFileSize(media.size)}</span>
                  </div>
                  <button
                    type="button"
                    title="Unduh file"
                    onClick={() => downloadKpiMedia(media.id, media.file_name || media.name)}
                    className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0 ml-2"
                  >
                    <Download size={15} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 py-6 text-center">Tidak ada file bukti yang diunggah.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── KpiItemsTable ────────────────────────────────────────────────────────────

interface KpiItemsTableProps {
  items: IApprovedKpiItem[];
  nameMap: NameMap;
  totalScore?: number | null;
  category?: string | null;
  renderManagerActual?: (item: IApprovedKpiItem, idx: number) => React.ReactNode;
}

export function KpiItemsTable({
  items,
  nameMap,
  totalScore,
  category,
  renderManagerActual,
}: Readonly<KpiItemsTableProps>) {
  const [evidenceItem, setEvidenceItem] = useState<IApprovedKpiItem | null>(null);

  if (items.length === 0) return null;

  return (
    <div>
      <h2 className="text-base font-semibold text-gray-800 mb-3">Daftar KPI</h2>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm" data-testid="kpi-table">
          <thead>
            <tr className="bg-gray-50 text-gray-500 text-xs font-medium uppercase tracking-wide">
              <th className="px-3 py-3 text-center whitespace-nowrap">No</th>
              <th className="px-3 py-3 text-left whitespace-nowrap">Aktivitas Utama</th>
              <th className="px-3 py-3 text-left whitespace-nowrap">Uraian Tugas</th>
              <th className="px-3 py-3 text-right whitespace-nowrap">Target</th>
              <th className="px-3 py-3 text-right whitespace-nowrap">Realisasi Karyawan</th>
              <th className="px-3 py-3 text-center whitespace-nowrap">Bukti</th>
              <th className="px-3 py-3 text-right whitespace-nowrap">Realisasi Manager</th>
              <th className="px-3 py-3 text-right whitespace-nowrap">AOB (%)</th>
              <th className="px-3 py-3 text-right whitespace-nowrap">Bobot</th>
              <th className="px-3 py-3 text-right whitespace-nowrap">Skor</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <React.Fragment key={item.id ?? idx}>
                <tr
                  key={item.id ?? idx}
                  className="bg-white hover:bg-gray-50 transition-colors border-t border-gray-100"
                >
                  <td className="px-3 py-3 text-center text-gray-500" rowSpan={item.notes ? 2 : 1}>
                    {idx + 1}
                  </td>
                  <td className="px-3 py-3 text-gray-800">
                    {nameMap.keyActivities[item.org_key_activity_id] || item.org_key_activity?.name || '-'}
                  </td>
                  <td className="px-3 py-3 text-gray-800">
                    {nameMap.jobDescs[item.org_job_description_id] || item.org_job_description?.name || '-'}
                  </td>
                  <td className="px-3 py-3 text-right text-gray-800">{item.target ?? '-'}</td>
                  <td className="px-3 py-3 text-right text-gray-800">
                    {item.actual_value !== null && item.actual_value !== undefined ? item.actual_value : '-'}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button
                      type="button"
                      title="Lihat bukti"
                      onClick={() => setEvidenceItem(item)}
                      className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 transition-colors"
                      data-testid={`evidence-btn-${item.id}`}
                    >
                      <Paperclip size={14} />
                      {item.media && item.media.length > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 text-[10px] font-bold text-white bg-blue-600 rounded-full">
                          {item.media.length}
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-right">
                    {renderManagerActual?.(item, idx) ?? (
                      <span className="text-gray-800">
                        {item.manager_actual !== null && item.manager_actual !== undefined ? item.manager_actual : '-'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right text-gray-800">
                    {item.aob !== null && item.aob !== undefined ? `${Number(item.aob).toFixed(1)}%` : '-'}
                  </td>
                  <td className="px-3 py-3 text-right text-gray-800">{item.weight ?? '-'}</td>
                  <td className="px-3 py-3 text-right text-gray-800">
                    {item.score !== null && item.score !== undefined ? Number(item.score).toFixed(2) : '-'}
                  </td>
                </tr>
                {item.notes && (
                  <tr key={`notes-${item.id ?? idx}`} className="bg-gray-50/50">
                    <td colSpan={9} className="px-3 py-2">
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">Catatan Karyawan:</span>{' '}
                        <span className="text-gray-600">{item.notes}</span>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap gap-6 justify-end pr-1">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 font-medium">Total Skor KPI:</span>
          <span className="text-sm font-semibold text-gray-800" data-testid="total-score">
            {totalScore !== null && totalScore !== undefined ? Number(totalScore).toFixed(2) : '-'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 font-medium">Kategori:</span>
          <span className="text-sm font-semibold text-gray-800" data-testid="category">
            {category ?? '-'}
          </span>
        </div>
      </div>

      {evidenceItem && <EvidenceModal item={evidenceItem} onClose={() => setEvidenceItem(null)} />}
    </div>
  );
}

// ─── ApprovedKpiView ──────────────────────────────────────────────────────────

interface ApprovedKpiViewProps {
  items: IApprovedKpiItem[];
  nameMap: NameMap;
  totalScore?: number | null;
  category?: string | null;
  notesManager?: string;
}

export function ApprovedKpiView({
  items,
  nameMap,
  totalScore,
  category,
  notesManager,
}: Readonly<ApprovedKpiViewProps>) {
  return (
    <>
      <KpiItemsTable items={items} nameMap={nameMap} totalScore={totalScore} category={category} />

      {notesManager !== undefined && (
        <div className="space-y-4 pb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Catatan Manajer</label>
            <textarea
              rows={3}
              readOnly
              value={notesManager || ''}
              placeholder="Tidak ada catatan dari manajer"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-600 resize-none cursor-default"
            />
          </div>
        </div>
      )}
    </>
  );
}
